///
/// EarthBrowser
///
/// Created by Matt Giger on 12/20/2024
/// Copyright (c) 2024 Geodata Labs
///

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Icons } from "@/components/ui/icons"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import type { LinkedAccountRecord } from "@shared-types/user"
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar"
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { AppleIcon, BadgeHelp, LockIcon, MoreHorizontal } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { useUnlinkAccountMutation, useSetPrimaryAccountMutation } from "@/redux/userApi"
import { useToast } from "@/components/ui/use-toast"
import { ViewAccountDialog } from "./ViewAccountDialog"

function ProviderIcon({ provider, method }: { provider: LinkedAccountRecord["provider"], method: LinkedAccountRecord["method"] }) {
	switch (provider) {
		case "google":
			return <div className="flex flex-col items-center justify-center gap-2">
				<Icons.googleLogo className="h-5 w-5" />
				<span className="text-xs">Google</span>
			</div>
		case "apple":
			return <div className="flex flex-col items-center justify-center gap-2">
				<AppleIcon className="h-5 w-5" />
				<span className="text-xs">Apple</span>
			</div>
		case "github":
			return <div className="flex flex-col items-center justify-center gap-2">
				<Icons.gitHub className="h-5 w-5" />
				<span className="text-xs">GitHub</span>
			</div>
		default:
			if (method === "password") {
				return <div className="flex flex-col items-center justify-center gap-2">
					<LockIcon className="h-5 w-5" />
					<span className="text-xs">Password</span>
				</div>
			}
			return <div className="flex flex-col items-center justify-center gap-2">
				<BadgeHelp className="h-5 w-5" />
				<span className="text-xs">Unknown</span>
			</div>
	}
}

export const createLinkedAccountColumns = (
	canUnlink: boolean,
	onViewAccount: (account: LinkedAccountRecord) => void,
	onSetPrimary: (accountId: number) => void,
	onUnlinkAccount: (account: LinkedAccountRecord) => void
): ColumnDef<LinkedAccountRecord>[] => [
	{
		accessorKey: "provider",
		cell: ({ row }) => {
			return <ProviderIcon provider={row.original.provider} method={row.original.method} />
		}
	},
	{
		accessorKey: "username",
		cell: ({ row }) => {
			const account = row.original
			return (
				<div className="flex flex-row gap-2 items-center">
					<Avatar className="h-6 w-6">
						{ account.image_url && account.image_url.length > 0 &&
							<>
								<AvatarImage className="rounded-full" src={account.image_url} alt={account.username} />
								<AvatarFallback>{account.username.charAt(0)}</AvatarFallback>
							</>
						}
					</Avatar>
					<span className="flex items-center">{account.username}</span>
					{account.is_primary && (
						<span className="text-xs text-blue-400 font-medium">Primary</span>
					)}
				</div>
			)
		}
	},
	{
		accessorKey: "email",
		header: "Email",
	},
	{
		accessorKey: "actions",
		cell: ({ row }) => {
			const account = row.original
			return <DropdownMenu>
				<DropdownMenuTrigger>
					<MoreHorizontal className="h-4 w-4" />
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					{account.method === "password" ? (
						<DropdownMenuItem onClick={() => {
							alert("Reset Password")
						}}>Reset Password</DropdownMenuItem>
					) : (
						<>
							<DropdownMenuItem onClick={() => onViewAccount(account)}>
								View Account...
							</DropdownMenuItem>
							{!account.is_primary && (
								<DropdownMenuItem onClick={() => onSetPrimary(account.id)}>
									Set as Primary
								</DropdownMenuItem>
							)}
							<DropdownMenuItem
								disabled={!canUnlink || account.is_primary}
								onClick={() => {
									if (canUnlink && !account.is_primary) {
										onUnlinkAccount(account)
									}
								}}
								className={account.is_primary ? "text-muted-foreground" : ""}
							>
								{account.is_primary
									? "Cannot unlink (primary account)"
									: canUnlink
									? "Unlink Account"
									: "Cannot unlink (last auth method)"}
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		}
	}
]

// Keep the old export for backwards compatibility, but default to canUnlink = true
export const linkedAccountColumns = createLinkedAccountColumns(true, () => {}, () => {}, () => {})

interface LinkedAccountTableProps<TData, TValue> {
	columns?: ColumnDef<TData, TValue>[]
	data: TData[]
	canUnlink?: boolean
}

export function LinkedAccountTable<TData, TValue>({ data, columns, canUnlink = true }: LinkedAccountTableProps<TData, TValue>) {
	const { toast } = useToast()
	const [viewAccountDialogOpen, setViewAccountDialogOpen] = useState(false)
	const [selectedAccount, setSelectedAccount] = useState<LinkedAccountRecord | null>(null)
	const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false)
	const [accountToUnlink, setAccountToUnlink] = useState<LinkedAccountRecord | null>(null)

	const [unlinkAccount, { isLoading: isUnlinking }] = useUnlinkAccountMutation()
	const [setPrimaryAccount] = useSetPrimaryAccountMutation()

	const handleViewAccount = useCallback((account: LinkedAccountRecord) => {
		setSelectedAccount(account)
		setViewAccountDialogOpen(true)
	}, [])

	const handleSetPrimary = useCallback(async (accountId: number) => {
		try {
			await setPrimaryAccount(accountId).unwrap()
			toast({
				title: "Primary account updated",
				description: "The account has been set as your primary account.",
			})
		} catch (error) {
			toast({
				title: "Failed to set primary account",
				description: error instanceof Error ? error.message : "An error occurred while setting the primary account.",
				variant: "destructive",
			})
		}
	}, [setPrimaryAccount, toast])

	const handleUnlinkClick = useCallback((account: LinkedAccountRecord) => {
		setAccountToUnlink(account)
		setUnlinkDialogOpen(true)
	}, [])

	const handleUnlinkConfirm = async () => {
		if (!accountToUnlink) return

		try {
			await unlinkAccount(accountToUnlink.id).unwrap()
			toast({
				title: "Account unlinked",
				description: "The account has been successfully unlinked.",
			})
			setUnlinkDialogOpen(false)
			setAccountToUnlink(null)
		} catch (error) {
			toast({
				title: "Failed to unlink account",
				description: error instanceof Error ? error.message : "An error occurred while unlinking the account.",
				variant: "destructive",
			})
		}
	}

	// Sort data with primary accounts first (memoized to prevent infinite re-renders)
	const sortedData = useMemo(() => {
		return [...data].sort((a, b) => {
			const aRecord = a as unknown as LinkedAccountRecord
			const bRecord = b as unknown as LinkedAccountRecord
			if (aRecord.is_primary && !bRecord.is_primary) return -1
			if (!aRecord.is_primary && bRecord.is_primary) return 1
			return 0
		})
	}, [data])

	// Use provided columns or generate them dynamically based on canUnlink (memoized)
	const tableColumns = useMemo(() => {
		return columns || (createLinkedAccountColumns(
			canUnlink,
			handleViewAccount,
			(accountId: number) => {
				void handleSetPrimary(accountId)
			},
			handleUnlinkClick
		) as unknown as ColumnDef<TData, TValue>[])
	}, [columns, canUnlink, handleViewAccount, handleSetPrimary, handleUnlinkClick])

	const table = useReactTable({
		data: sortedData,
		columns: tableColumns,
		getCoreRowModel: getCoreRowModel()
	})

	return (
		<>
			<div className="w-full">
				<Table>
					<TableBody>
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map((row) => {
							const account = row.original as unknown as LinkedAccountRecord
							const isPrimary = account.is_primary
							return (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
									className={
										isPrimary
											? "bg-blue-500/5 border-l-2 border-l-blue-500 border-b-blue-500/20 hover:bg-blue-500/10 transition-colors"
											: "border-blue-500/10 hover:bg-blue-500/5 transition-colors"
									}
								>
									{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
									))}
								</TableRow>
							)
						})
					) : (
						<TableRow>
						<TableCell colSpan={tableColumns?.length ?? 0} className="h-24 text-center">
							None
						</TableCell>
						</TableRow>
					)}
					</TableBody>
				</Table>
			</div>

			<ViewAccountDialog
				account={selectedAccount}
				open={viewAccountDialogOpen}
				onOpenChange={setViewAccountDialogOpen}
			/>

			<AlertDialog open={unlinkDialogOpen} onOpenChange={setUnlinkDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Unlink Account</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to unlink this {accountToUnlink?.provider} account?
							You will no longer be able to sign in using this account.
							{accountToUnlink && (
								<span className="block mt-2 font-medium">
									Account: {accountToUnlink.email || accountToUnlink.username}
								</span>
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isUnlinking}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleUnlinkConfirm}
							disabled={isUnlinking}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isUnlinking ? "Unlinking..." : "Unlink Account"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
